import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Image,
  LayoutChangeEvent,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';

import { adUnits } from '../constants';
import JobsContext from '../context/JobsContext';
import { track } from '../lib/analytics';
import { Palette, radius, shadow, spacing, useThemePalette } from '../theme';

// Lazy native ads module — same pattern as AdBanner. Avoids resolving
// the SDK on web, where the package's native components throw on import.
type AdsModule = {
  NativeAd: {
    createForAdRequest: (
      adUnitId: string
    ) => Promise<NativeAdInstance>;
  };
  NativeAdView: React.ComponentType<{
    nativeAd: NativeAdInstance;
    style?: object;
    onLayout?: (event: LayoutChangeEvent) => void;
    children?: React.ReactNode;
  }>;
  NativeAsset: React.ComponentType<{
    assetType: string;
    children: React.ReactElement;
  }>;
  NativeMediaView: React.ComponentType<{
    style?: object;
    resizeMode?: 'cover' | 'contain' | 'stretch';
  }>;
  NativeAssetType: Record<string, string>;
  NativeAdEventType: Record<string, string>;
};

type NativeAdInstance = {
  headline: string;
  body: string;
  callToAction: string;
  advertiser: string | null;
  icon: { url: string } | null;
  mediaContent: { aspectRatio?: number | null; hasVideoContent?: boolean } | null;
  addAdEventListener: (
    type: string,
    listener: (payload?: unknown) => void
  ) => { remove: () => void };
  removeAllAdEventListeners: () => void;
  destroy: () => void;
};

let ads: AdsModule | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    ads = require('react-native-google-mobile-ads');
  } catch (err) {
    if (__DEV__) console.warn('[ads] NativeAd module unavailable:', err);
  }
}

const NativeAdCard: React.FC = () => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { hideAds, removeAdsEntitled } = useContext(JobsContext);
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [ad, setAd] = useState<NativeAdInstance | null>(null);
  const adsHidden = hideAds || removeAdsEntitled;
  // iOS subpixel heights (e.g. 637.333…) cause AdMob's native-ad validator
  // to flag "Advertiser assets outside native ad view" — the asset frames
  // round one way and the parent rounds the other, so children come out
  // a fraction of a pixel past the parent's bounds. Lock the height to
  // an integer after first layout. See invertase/react-native-google-
  // mobile-ads#700.
  const [pinnedHeight, setPinnedHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!ads || adsHidden) return;

    let mounted = true;
    let loaded: NativeAdInstance | null = null;

    ads.NativeAd.createForAdRequest(adUnits.native)
      .then((nativeAd) => {
        if (!mounted) {
          nativeAd.destroy();
          return;
        }
        loaded = nativeAd;
        nativeAd.addAdEventListener(ads!.NativeAdEventType.IMPRESSION, () =>
          track('ad_impression', { format: 'native' })
        );
        nativeAd.addAdEventListener(ads!.NativeAdEventType.CLICKED, () =>
          track('cliked_on_ad', { format: 'native' })
        );
        setAd(nativeAd);
      })
      .catch((err) => {
        track('ad_load_failed', {
          format: 'native',
          error: err instanceof Error ? err.message : String(err),
        });
        if (__DEV__) console.warn('[ads] native ad load failed:', err);
      });

    return () => {
      mounted = false;
      loaded?.destroy();
    };
  }, [adsHidden]);

  if (adsHidden || !ads || !ad) return null;

  const { NativeAdView, NativeAsset, NativeMediaView, NativeAssetType } = ads;
  // AdMob's mediaContent.aspectRatio is width/height. Fall back to 16:9
  // when the SDK doesn't surface one (rare, but possible for image-only
  // creatives without a declared ratio).
  const mediaAspectRatio = ad.mediaContent?.aspectRatio || 16 / 9;

  return (
    <NativeAdView
      nativeAd={ad}
      style={[styles.card, pinnedHeight != null ? { height: pinnedHeight } : null]}
      onLayout={(e: LayoutChangeEvent) => {
        if (pinnedHeight == null) {
          setPinnedHeight(Math.ceil(e.nativeEvent.layout.height));
        }
      }}
    >
      <View style={styles.sponsoredBadge}>
        <Text style={styles.sponsoredText}>{t('ad.sponsored')}</Text>
      </View>

      <View style={styles.headerRow}>
        <View style={styles.headerText}>
          <NativeAsset assetType={NativeAssetType.HEADLINE}>
            <Text style={styles.title} numberOfLines={2}>
              {ad.headline}
            </Text>
          </NativeAsset>
          {ad.advertiser ? (
            <NativeAsset assetType={NativeAssetType.ADVERTISER}>
              <Text style={styles.company} numberOfLines={1}>
                {ad.advertiser}
              </Text>
            </NativeAsset>
          ) : null}
        </View>
        {ad.icon?.url ? (
          <NativeAsset assetType={NativeAssetType.ICON}>
            <Image source={{ uri: ad.icon.url }} style={styles.icon} />
          </NativeAsset>
        ) : null}
      </View>

      <NativeMediaView
        style={[styles.media, { aspectRatio: mediaAspectRatio }]}
        resizeMode="cover"
      />

      {ad.body ? (
        <NativeAsset assetType={NativeAssetType.BODY}>
          <Text style={styles.body} numberOfLines={2}>
            {ad.body}
          </Text>
        </NativeAsset>
      ) : null}

      <NativeAsset assetType={NativeAssetType.CALL_TO_ACTION}>
        <View style={styles.cta}>
          <Text style={styles.ctaText}>{ad.callToAction}</Text>
        </View>
      </NativeAsset>
    </NativeAdView>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      marginHorizontal: spacing.lg,
      marginTop: spacing.md,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      ...shadow.card,
    },
    media: {
      width: '100%',
      marginTop: spacing.sm,
      borderRadius: radius.sm,
      backgroundColor: p.surfaceMuted,
    },
    sponsoredBadge: {
      alignSelf: 'flex-start',
      backgroundColor: p.surfaceMuted,
      paddingHorizontal: spacing.sm,
      paddingVertical: 2,
      borderRadius: radius.sm,
      marginBottom: spacing.xs,
    },
    sponsoredText: {
      fontSize: 10,
      fontWeight: '600',
      color: p.textMuted,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    headerText: { flex: 1, paddingRight: spacing.sm },
    title: { fontSize: 16, fontWeight: '700', color: p.textPrimary },
    company: { fontSize: 13, color: p.textSecondary, marginTop: 2 },
    icon: {
      width: 40,
      height: 40,
      borderRadius: radius.sm,
      backgroundColor: p.surfaceMuted,
    },
    body: {
      fontSize: 13,
      color: p.textSecondary,
      marginTop: spacing.sm,
    },
    cta: {
      alignSelf: 'stretch',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: p.primary,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      borderRadius: radius.pill,
      marginTop: spacing.md,
      // Material Design / Apple HIG minimum touch target for comfortable
      // thumb taps.
      minHeight: 48,
    },
    // Always-dark text on the green primary CTA — same convention used in
    // PaperProvider's onPrimary override.
    ctaText: {
      fontSize: 13,
      fontWeight: '700',
      color: '#0F1115',
    },
  });

export default NativeAdCard;
